<script lang="ts">
  // Big head-to-head card (VoteHub style). `alt` is the same office at the other scope (chs-X <-> sc-X):
  // a Charleston | Statewide toggle switches between them so the two are never mixed.
  import type { ChoiceSummary, RaceSummary } from '../../types';
  import { app } from '../../lib/app.svelte';
  import { fmtInt, fmtPct } from '../../lib/format';
  import { cardName, hasVotes, isWriteIn, marginRange, marginText, resolvePair, scopeLabel, verdict, winMarks } from '../../lib/race';
  import WinMark from '../atoms/WinMark.svelte';
  import Avatar from '../atoms/Avatar.svelte';
  import PartyBadge from '../atoms/PartyBadge.svelte';
  import StatusPill from '../atoms/StatusPill.svelte';
  import Est from '../atoms/Est.svelte';
  import SplitBar from './SplitBar.svelte';

  let { race, alt = null }: { race: RaceSummary; alt?: RaceSummary | null } = $props();
  let showAlt = $state(false);
  const r = $derived(showAlt && alt ? alt : race);
  const pair = $derived(resolvePair(r) ?? (r.choices.length >= 2 ? ([r.choices[0]!, r.choices[1]!] as [ChoiceSummary, ChoiceSummary]) : null));
  const others = $derived(pair ? r.choices.filter((c) => c !== pair[0] && c !== pair[1]) : []);
  const othersPct = $derived(others.reduce((a, c) => a + c.pct, 0));
  const pc = $derived(r.pct_expected_counted);
  const any = $derived(hasVotes(r));
  const unitWord = $derived(r.unit_kind === 'county' ? 'counties' : 'precincts');
  const showEst = $derived(r.show_needle && !!r.margin && r.vote_for === 1);
  const marks = $derived(winMarks(r));
</script>

<article class="card h2h">
  <header>
    <div class="ttl">
      <div class="kicker">{scopeLabel(r, true)}</div>
      <h3><a href={app.href(`#/race/${encodeURIComponent(r.race_id)}`)}>{r.title}</a></h3>
    </div>
    <div class="right">
      <StatusPill race={r} />
    </div>
  </header>
  {#if alt}
    <div class="seg scope" role="group" aria-label="Show results for">
      <button type="button" aria-pressed={!showAlt} onclick={() => (showAlt = false)}>{scopeLabel(race)}</button>
      <button type="button" aria-pressed={showAlt} onclick={() => (showAlt = true)}>{scopeLabel(alt)}</button>
    </div>
  {/if}

  {#if pair}
    <div class="duo">
      {#each [pair[0], pair[1]] as c, i (c.choice_id)}
        <div class="cand" class:right={i === 1}>
          <Avatar choice={c} size={52} />
          <div class="who">
            <div class="name">{cardName(c, r.kind)} <PartyBadge party={c.party} color={c.color} /><WinMark kind={marks.get(c.choice_id)} compact /></div>
            <div class="big num" class:none={!any} style="color:var(--c-{c.color})">{any ? fmtPct(c.pct) : '—'}</div>
            {#if any}<div class="votes num">{fmtInt(c.votes)} votes</div>{/if}
          </div>
        </div>
      {/each}
    </div>
    {#if any}
    <SplitBar left={pair[0]} right={pair[1]} margin={showEst ? r.margin : null} />
    <div class="legend">
      {#if any}<span class="k"><i class="sq"></i>Counted so far</span>{/if}
      {#if showEst && r.margin}
        <span class="kb est-text"><i class="dia"></i> Estimated final: <b>{marginText(r.margin.median, pair, r.kind)}</b> <span class="rng">({marginRange(r.margin, pair, r.kind)})</span></span>
      {:else if r.reason_no_needle && r.status !== 'uncontested'}
        <span class="k muted">{r.reason_no_needle}</span>
      {/if}
    </div>
    {/if}
  {/if}

  <p class="verdict">{verdict(r)}</p>

  <footer>
    {#if pc}
      <span>≈{fmtPct(pc.median, 0)} of expected vote counted <Est /></span>
    {/if}
    <span class="muted">{fmtInt(r.units_reported)}/{fmtInt(r.units_total)} {unitWord} reporting</span>
    {#if othersPct >= 0.1}<span class="muted">Others {fmtPct(othersPct)}{others.some((o) => !isWriteIn(o)) ? '' : ' (write-ins)'}</span>{/if}
  </footer>
</article>

<style>
  .h2h { display: flex; flex-direction: column; gap: 14px; container-type: inline-size; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
  .ttl { min-width: 0; flex: 1; }
  h3 { font-size: 1.35rem; margin-top: 5px; }
  h3 a { color: inherit; }
  .scope { align-self: flex-start; }
  .duo { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .cand { display: flex; gap: 12px; align-items: center; min-width: 0; }
  .cand.right { flex-direction: row-reverse; text-align: right; }
  .who { min-width: 0; }
  .name { font-weight: 600; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; font-size: 0.95rem; }
  .right .name { justify-content: flex-end; }
  .big { font: 700 clamp(2rem, 1.4rem + 2vw, 2.75rem) / 1 var(--display); letter-spacing: -0.02em; margin-top: 4px; }
  .big.none { color: var(--ink-4) !important; }
  .votes { color: var(--ink-3); font-size: 0.85rem; margin-top: 3px; }
  .legend { display: flex; flex-wrap: wrap; gap: 4px 16px; font-size: 0.82rem; color: var(--ink-2); margin-top: -6px; }
  .k { display: inline-flex; align-items: center; gap: 6px; }
  .sq { width: 14px; height: 8px; border-radius: 4px; background: linear-gradient(90deg, var(--c-dem) 50%, var(--c-rep) 50%); }
  .kb { display: inline; }
  .dia { display: inline-block; width: 9px; height: 9px; background: var(--est); transform: rotate(45deg); border-radius: 1px; margin-right: 3px; vertical-align: 0; }
  .rng { color: var(--ink-3); font-weight: 400; }
  .verdict { margin: 0; font: 600 1.02rem/1.35 var(--display); color: var(--ink); }
  footer { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 0.82rem; color: var(--ink-2); border-top: 1px solid var(--line); padding-top: 12px; margin-top: auto; }
  /* narrow cards (2-up grid, phones): stack avatar over name, smaller numbers */
  @container (max-width: 470px) {
    .cand { flex-direction: column; align-items: flex-start; gap: 8px; }
    .cand.right { flex-direction: column; align-items: flex-end; }
    .big { font-size: clamp(1.9rem, 9cqi, 2.5rem); }
    .name { font-size: 0.9rem; }
  }
</style>
